import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

type PromptDomain = "sa" | "ua" | "iw" | "general";

type SamplePromptEntry = {
  domain: PromptDomain;
  message: MessageDescriptor;
};

/**
 * ドメイン別の静的プロンプトプール。
 *
 * LLM 生成が未完了・失敗時のフォールバックとして使用する。
 * ワークスペースに存在するプロジェクトタイプに応じてフィルタする。
 */
const STATIC_PROMPT_POOL: SamplePromptEntry[] = [
  // Search Analytics
  {
    domain: "sa",
    message: msg`List the top 10 queries by clicks from last month's GSC data.`,
  },
  {
    domain: "sa",
    message: msg`Using last month's GSC data, identify queries with the most room for improvement and suggest how to improve them.`,
  },
  {
    domain: "sa",
    message: msg`Show the top pages by impressions from the last 28 days in GSC.`,
  },
  {
    domain: "sa",
    message: msg`Compare this month's GSC click-through rate with last month and highlight changes.`,
  },
  {
    domain: "sa",
    message: msg`Find queries where average position improved but clicks decreased.`,
  },
  {
    domain: "sa",
    message: msg`Create a Search Analytics view filtered to mobile users only.`,
  },
  {
    domain: "sa",
    message: msg`Which pages lost the most impressions compared to last month?`,
  },

  // User Analytics
  {
    domain: "ua",
    message: msg`Compare sessions and conversion rate by default channel group in GA4 over the last 28 days.`,
  },
  {
    domain: "ua",
    message: msg`Show the top 10 landing pages by sessions from last month's GA4 data.`,
  },
  {
    domain: "ua",
    message: msg`Which traffic sources had the highest bounce rate in the last 28 days?`,
  },
  {
    domain: "ua",
    message: msg`Compare this week's GA4 session count with last week by device category.`,
  },
  {
    domain: "ua",
    message: msg`Show user engagement metrics trend for the last 3 months.`,
  },
  {
    domain: "ua",
    message: msg`Which pages have the highest exit rate in the last 28 days?`,
  },

  // Index Worker
  {
    domain: "iw",
    message: msg`Show the status of my Index Worker jobs and highlight any failures.`,
  },
  {
    domain: "iw",
    message: msg`Which URLs failed indexing most recently and what were the reasons?`,
  },
  {
    domain: "iw",
    message: msg`Create a new Index Worker for sitemap-based URL submissions.`,
  },
  {
    domain: "iw",
    message: msg`Summarize the indexing success rate over the past week.`,
  },

  // General
  {
    domain: "general",
    message: msg`What data sources are connected to this workspace?`,
  },
  {
    domain: "general",
    message: msg`Give me an overview of this workspace's analytics setup.`,
  },
  {
    domain: "general",
    message: msg`What are the most important metrics I should track for my website?`,
  },
  {
    domain: "general",
    message: msg`How can I use Amethyst AI to improve my site's performance?`,
  },
];

type AvailableDomains = {
  hasSA: boolean;
  hasUA: boolean;
  hasIW: boolean;
};

/**
 * ワークスペースのプロジェクト構成に応じて静的プロンプトをフィルタし、
 * SSR と hydration で同じ候補を返す。
 */
export function selectFallbackPrompts(
  domains: AvailableDomains,
  count: number,
): MessageDescriptor[] {
  const eligible = STATIC_PROMPT_POOL.filter((entry) => {
    switch (entry.domain) {
      case "sa":
        return domains.hasSA;
      case "ua":
        return domains.hasUA;
      case "iw":
        return domains.hasIW;
      case "general":
        return true;
    }
  });

  if (eligible.length === 0) {
    return STATIC_PROMPT_POOL.filter((e) => e.domain === "general")
      .slice(0, count)
      .map((e) => e.message);
  }

  // 利用可能なドメインから均等に取る
  const activeDomains = (
    ["sa", "ua", "iw", "general"] as PromptDomain[]
  ).filter((d) => {
    if (d === "sa") return domains.hasSA;
    if (d === "ua") return domains.hasUA;
    if (d === "iw") return domains.hasIW;
    return true;
  });

  const selected: SamplePromptEntry[] = [];
  const usedDomains = new Set<PromptDomain>();

  for (const domain of activeDomains) {
    if (selected.length >= count) break;
    const candidate = eligible.find(
      (e) => e.domain === domain && !selected.includes(e),
    );
    if (candidate) {
      selected.push(candidate);
      usedDomains.add(domain);
    }
  }

  for (const entry of eligible) {
    if (selected.length >= count) break;
    if (!selected.includes(entry)) {
      selected.push(entry);
    }
  }

  return selected.slice(0, count).map((e) => e.message);
}
